'use client'

import { usePathname } from 'next/navigation'
import { useEffect, type ReactNode } from 'react'
import { FloatingLanguageSwitcher } from '@/components/language-switcher'

/**
 * Conditionally renders the player nav/header + footer. They are HIDDEN on the
 * sign-in surface (`/play/auth*`) — an unauthenticated login page must not show
 * the learner nav bar (Home / Play / Library / …). Everywhere else inside the
 * player portal the full chrome renders normally.
 */
export function PlayerChrome({
  header,
  footer,
  children,
}: {
  header: ReactNode
  footer: ReactNode
  children: ReactNode
}) {
  const pathname = usePathname() || ''
  const bare = pathname.startsWith('/play/auth')

  // Publish the nav's height as --play-nav-h so a SECOND sticky bar can pin
  // below it instead of underneath it.
  //
  // Both bars used to be `sticky top-0`, which pins them to the same offset;
  // the in-game mode bar is z-30 against this nav's z-40, so on the first
  // scroll it slid behind the nav and disappeared (QA round 2, item 4).
  //
  // Measured rather than hardcoded: this nav contains the tenant logo, sized
  // from whatever file the tenant uploaded, and it changed height the same week
  // (h-10 to h-12, QA item 7). A literal offset would already be stale.
  //
  // Measured by SELECTOR, not a ref on a wrapper: `position: sticky` is
  // confined to its containing block, so wrapping the header in a div just to
  // hold a ref would shrink that block to the header's own height and stop it
  // sticking at all.
  useEffect(() => {
    if (typeof window === 'undefined') return
    const el = document.querySelector<HTMLElement>('[data-play-nav]')
    if (!el) return
    const apply = () =>
      document.documentElement.style.setProperty(
        '--play-nav-h',
        `${el.getBoundingClientRect().height}px`,
      )
    apply()
    const ro = new ResizeObserver(apply)
    ro.observe(el)
    return () => ro.disconnect()
  }, [bare])

  if (bare) {
    // Login surface — center the card in the viewport, no nav, no footer. The
    // language switcher stays: this is the first screen an invitee sees.
    return (
      <main className="flex-1 flex flex-col">
        <FloatingLanguageSwitcher />
        {children}
      </main>
    )
  }

  return (
    <>
      {header}
      <main className="flex-1">{children}</main>
      {footer}
    </>
  )
}
